import { DestroyRef, Injectable, Signal, inject, signal } from "@angular/core";

// Viewport listeners shared by every component instance that needs them.
//
// Every listener or observer callback is followed by its own microtask
// checkpoint, so a signal written from N per-instance callbacks costs N app
// ticks, while one shared callback writing all of them costs one. A quote with
// many routes renders dozens of tables, which turned a single resize frame into
// dozens of full change-detection passes.
@Injectable({ providedIn: "root" })
export class ViewportService {
  private readonly mobile = signal(false);
  public readonly isMobile: Signal<boolean> = this.mobile.asReadonly();

  private readonly resizeCallbacks = new Map<Element, () => void>();
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    const mobileQuery = window.matchMedia("(max-width: 767px)");
    this.mobile.set(mobileQuery.matches);
    const onChange = (event: MediaQueryListEvent) =>
      this.mobile.set(event.matches);
    mobileQuery.addEventListener("change", onChange);
    inject(DestroyRef).onDestroy(() =>
      mobileQuery.removeEventListener("change", onChange),
    );
  }

  // Calls `callback` at most once per frame when any of `elements` resizes.
  // The callback runs outside the Angular zone: write signals, and the writes
  // are picked up by a single scheduled tick. Returns the disposer.
  public observeResize(elements: Element[], callback: () => void): () => void {
    this.resizeObserver ??= new ResizeObserver((entries) => {
      const callbacks = new Set(
        entries.map((entry) => this.resizeCallbacks.get(entry.target)),
      );
      callbacks.forEach((resized) => resized?.());
    });

    for (const element of elements) {
      this.resizeCallbacks.set(element, callback);
      this.resizeObserver.observe(element);
    }

    return () => {
      for (const element of elements) {
        this.resizeObserver?.unobserve(element);
        this.resizeCallbacks.delete(element);
      }
    };
  }
}
